import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { differenceInHours, format } from 'date-fns';

import { send } from 'loot-core/platform/client/fetch';

import { useActions } from '../hooks/useActions';
import { useLocalPref } from '../hooks/useLocalPref';
import { AsyncStorageKey, useStorage } from '../hooks/useStorage';

import { Button, ButtonWithLoading } from './common/Button';
import { Modal } from './common/Modal';
import { View } from './common/View';
import { Paragraph } from './common/Paragraph';
import { useDemoSite } from '../hooks/useDemoSite';
import { Link } from "./common/Link";
import { theme } from '../style';

export function DemoBudgetDateLimit() {
  const { getItem: getDemoBudgetStartDate } = useStorage(AsyncStorageKey.DEMO_BUDGET_START_DATE);
  const { isDemoSite } = useDemoSite();
  const [budgetId] = useLocalPref('id');
  const navigate = useNavigate();
  const [isExportLoading, setExportIsLoading] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const { closeBudget } = useActions();

  (async () => {
    const demoBudgetStartDate = await getDemoBudgetStartDate();

    if (
      isDemoSite &&
      differenceInHours(new Date(), new Date(demoBudgetStartDate)) >= 1
    ) {
      setShowModal(true);
    }
  })();

  async function onExport() {
    setExportIsLoading(true);

    const response = await send('export-budget');

    if ('error' in response) {
      setExportIsLoading(false);
      console.log('Export error code:', response.error);
      return;
    }

    window.Actual?.saveFile(
      response.data,
      `${format(new Date(), 'yyyy-MM-dd')}-${budgetId}.zip`,
      'Export budget',
    );
    setExportIsLoading(false);
  }

  async function onDemoBudgetReset() {
    await closeBudget();
    navigate('/post-demo?action=reset&budgetId=' + budgetId);
  }

  async function onSignup() {
    await closeBudget();
    navigate('/post-demo?action=signup');
  }

  return showModal ? (
    <Modal
      style={{ maxWidth: '400' }}
      isCurrent={true}
      showClose={false}
      stackIndex={0}
      title="Demo Time Expired"
    >
      <View>
        <Paragraph>Your demo time has expired. Please sign up to continue using the app.</Paragraph>
        <Paragraph>
          If you've started building your budget during the demo, you can <Link variant={'text'} style={{ fontSize: 14, color: theme.pageTextLink }} onClick={() => onExport()}>export</Link> a copy to save it. You can later import this copy after signing up.
        </Paragraph>
        <Paragraph>
          Alternatively, you can reset the demo and start over.
        </Paragraph>
        <View style={{ flexDirection: 'row', gap: 5 }}>
          <Button
            type={'primary'}
            onClick={onSignup}
          >
            Signup
          </Button>
          <ButtonWithLoading
            disabled={isExportLoading}
            onClick={onDemoBudgetReset}
          >
            Reset demo
          </ButtonWithLoading>
        </View>
      </View>
    </Modal>
  ) : null;
}
